import { useEffect } from 'react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * BrandTheme component that injects brand-specific CSS custom properties
 * This allows the existing CSS to work with both brands dynamically
 */
export const BrandTheme: React.FC = () => {
  const brand = useBrand();
  const { isDark } = useTheme();

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    if (brand.key === 'antigreed') {
      // AntiGreed.app color scheme - Cream/beige theme for better contrast
      root.style.setProperty('--matrix-green', '45 60% 85%'); // Cream/beige in HSL
      root.style.setProperty('--blood-red', '25 100% 50%'); // Warning orange in HSL
      root.style.setProperty('--terminal-green', '42 50% 75%'); // Light cream in HSL
      root.style.setProperty('--primary', '45 60% 85%'); // Cream primary
      root.style.setProperty('--destructive', '25 100% 50%'); // Warning orange destructive
      root.style.setProperty('--background', '120 20% 8%'); // Very dark green background
      root.style.setProperty('--border', '45 30% 40%'); // Cream borders with lower brightness
      root.style.setProperty('--ring', '45 60% 85%'); // Cream focus rings

      // Set custom gradient background for antigreed.app
      body.style.background = 'linear-gradient(45deg, #1b604b3d, transparent)';
      body.style.backgroundColor = '';
      body.classList.remove('brand-ahp');

      // Update favicon and app icon for AntiGreed
      updateFavicon('/antigreed-icon.png');
      updateAppleTouchIcon('/antigreed-icon.png');
      updateMetaThemeColor('#1a5a3a'); // Forest green
    } else if (brand.key === 'ahp') {
      // American Hospital Prices — Patriotic red/white/blue theme (light) or dark navy (dark mode)
      // Green used ONLY for positive money values via inline styles

      // Core brand colors — always the same regardless of mode
      root.style.setProperty('--blood-red', '355 69% 42%'); // Old Glory Red #B22234
      root.style.setProperty('--terminal-green', '155 36% 35%'); // Money green only
      root.style.setProperty('--corruption-gray', '0 0% 60%');
      root.style.setProperty('--warning-amber', '42 38% 53%'); // Presidential gold #C5A55A

      // Radius
      root.style.setProperty('--radius', '0.5rem');

      // Primary — Old Glory Red (always)
      root.style.setProperty('--primary', '355 69% 42%'); // #B22234
      root.style.setProperty('--primary-foreground', '0 0% 100%');

      // Destructive — same red
      root.style.setProperty('--destructive', '355 69% 42%');
      root.style.setProperty('--destructive-foreground', '0 0% 100%');

      if (isDark) {
        // ── AHP DARK MODE — navy theme matching the loading skeleton ──────
        root.style.setProperty('--matrix-green', '42 38% 53%');     // Gold as primary UI color
        root.style.setProperty('--background', '216 60% 10%');       // #0a1628 navy
        root.style.setProperty('--foreground', '40 33% 96%');        // warm white
        root.style.setProperty('--card', '217 55% 14%');             // slightly lighter navy
        root.style.setProperty('--card-foreground', '40 33% 96%');
        root.style.setProperty('--popover', '217 55% 14%');
        root.style.setProperty('--popover-foreground', '40 33% 96%');
        root.style.setProperty('--secondary', '217 45% 18%');
        root.style.setProperty('--secondary-foreground', '40 33% 96%');
        root.style.setProperty('--muted', '217 40% 16%');
        root.style.setProperty('--muted-foreground', '217 15% 55%');
        root.style.setProperty('--accent', '217 45% 18%');
        root.style.setProperty('--accent-foreground', '40 33% 96%');
        root.style.setProperty('--border', '217 30% 22%');
        root.style.setProperty('--input', '217 45% 18%');
        root.style.setProperty('--ring', '42 38% 53%');              // gold focus ring

        root.style.setProperty('--shadow-matrix', '0 2px 12px rgba(10,22,40,0.6)');
        root.style.setProperty('--shadow-corruption', '0 2px 12px rgba(178,34,52,0.2)');
        root.style.setProperty('--shadow-terminal', '0 2px 16px rgba(0,0,0,0.4)');
        root.style.setProperty('--gradient-matrix', 'linear-gradient(135deg, rgba(197,165,90,0.06), rgba(10,22,40,0.4))');
        root.style.setProperty('--gradient-corruption', 'linear-gradient(90deg, #0a1628, rgba(178,34,52,0.08), #0a1628)');
        root.style.setProperty('--gradient-terminal', 'linear-gradient(180deg, #0d1e35, #0a1628)');

        body.style.background = '';
        body.style.backgroundColor = '#0a1628';
        body.className = body.className.replace(/\bdark\b/g, '').replace(/\bbrand-\w+\b/g, '').trim();
        body.classList.add('brand-ahp', 'brand-ahp-dark');
      } else {
        // ── AHP LIGHT MODE — warm white / patriotic palette ──────────────
        root.style.setProperty('--matrix-green', '217 100% 20%');    // Navy #002868 for UI elements
        root.style.setProperty('--background', '40 20% 98%');         // Warm white #FAF9F6
        root.style.setProperty('--foreground', '0 0% 11%');           // Near-black #1B1B1B
        root.style.setProperty('--card', '0 0% 100%');
        root.style.setProperty('--card-foreground', '0 0% 11%');
        root.style.setProperty('--popover', '0 0% 100%');
        root.style.setProperty('--popover-foreground', '0 0% 11%');
        root.style.setProperty('--secondary', '40 15% 94%');
        root.style.setProperty('--secondary-foreground', '0 0% 11%');
        root.style.setProperty('--muted', '40 15% 94%');
        root.style.setProperty('--muted-foreground', '0 0% 35%');
        root.style.setProperty('--accent', '355 30% 96%');
        root.style.setProperty('--accent-foreground', '0 0% 11%');
        root.style.setProperty('--border', '40 10% 88%');
        root.style.setProperty('--input', '40 10% 93%');
        root.style.setProperty('--ring', '355 69% 42%');              // Red focus ring

        root.style.setProperty('--shadow-matrix', '0 2px 12px rgba(0,40,104,0.08)');
        root.style.setProperty('--shadow-corruption', '0 2px 12px rgba(178,34,52,0.1)');
        root.style.setProperty('--shadow-terminal', '0 2px 16px rgba(0,0,0,0.06)');
        root.style.setProperty('--gradient-matrix', 'linear-gradient(135deg, rgba(0,40,104,0.04), rgba(0,40,104,0.01))');
        root.style.setProperty('--gradient-corruption', 'linear-gradient(90deg, #FAF9F6, rgba(178,34,52,0.05), #FAF9F6)');
        root.style.setProperty('--gradient-terminal', 'linear-gradient(180deg, #FFFFFF, #FAF9F6)');

        body.style.background = '';
        body.style.backgroundColor = '#FAF9F6';
        body.className = body.className.replace(/\bdark\b/g, '').replace(/\bbrand-\w+\b/g, '').trim();
        body.classList.add('brand-ahp');
      }

      // Update favicon and app icon
      updateFavicon('/consumer/favicon.svg');
      updateAppleTouchIcon('/consumer/favicon.svg');
      updateMetaThemeColor('#B22234'); // Old Glory Red for mobile browser chrome
    } else {
      // Luigi's Hall of Shame - Original matrix green theme
      root.style.setProperty('--matrix-green', '120 100% 50%'); // Matrix green in HSL
      root.style.setProperty('--blood-red', '0 100% 50%'); // Blood red in HSL
      root.style.setProperty('--terminal-green', '120 100% 80%'); // Terminal green in HSL
      root.style.setProperty('--primary', '120 100% 50%'); // Green primary
      root.style.setProperty('--destructive', '0 100% 50%'); // Red destructive
      root.style.setProperty('--background', '0 0% 4%'); // Deep black
      root.style.setProperty('--border', '120 30% 20%'); // Green borders
      root.style.setProperty('--ring', '120 100% 50%'); // Green focus rings

      // Set standard background for Luigi's
      body.style.background = '';
      body.style.backgroundColor = 'hsl(var(--background))';
      body.classList.remove('brand-ahp');

      // Update favicon and app icon for Luigi's
      updateFavicon('/src/assets/app-icon.png');
      updateAppleTouchIcon('/src/assets/app-icon.png');
      updateMetaThemeColor('#000000'); // Black
    }
  }, [brand.key, isDark]);

  // Helper function to update favicon
  const updateFavicon = (iconPath: string) => {
    // Update or create favicon link tags
    let icon16 = document.querySelector('link[rel="icon"][sizes="16x16"]') as HTMLLinkElement;
    let icon32 = document.querySelector('link[rel="icon"][sizes="32x32"]') as HTMLLinkElement;
    let iconDefault = document.querySelector('link[rel="icon"]:not([sizes])') as HTMLLinkElement;

    if (!icon16) {
      icon16 = document.createElement('link');
      icon16.rel = 'icon';
      icon16.type = 'image/png';
      icon16.sizes = '16x16';
      document.head.appendChild(icon16);
    }
    if (!icon32) {
      icon32 = document.createElement('link');
      icon32.rel = 'icon';
      icon32.type = 'image/png';
      icon32.sizes = '32x32';
      document.head.appendChild(icon32);
    }
    if (!iconDefault) {
      iconDefault = document.createElement('link');
      iconDefault.rel = 'icon';
      iconDefault.type = 'image/png';
      document.head.appendChild(iconDefault);
    }

    icon16.href = iconPath;
    icon32.href = iconPath;
    iconDefault.href = iconPath;
  };

  // Helper function to update apple touch icon
  const updateAppleTouchIcon = (iconPath: string) => {
    let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement;

    if (!appleTouchIcon) {
      appleTouchIcon = document.createElement('link');
      appleTouchIcon.rel = 'apple-touch-icon';
      document.head.appendChild(appleTouchIcon);
    }

    appleTouchIcon.href = iconPath;
  };

  // Helper function to update meta theme color
  const updateMetaThemeColor = (color: string) => {
    let themeColorMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement;

    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta');
      themeColorMeta.name = 'theme-color';
      document.head.appendChild(themeColorMeta);
    }

    themeColorMeta.content = color;
  };

  return null; // This is a utility component that doesn't render anything
};

/**
 * Hook to get brand-specific color values for inline styles
 */
export const useBrandColors = () => {
  const brand = useBrand();

  return {
    primary: brand.colors.primary,
    secondary: brand.colors.secondary,
    accent: brand.colors.accent,
    text: brand.colors.text,
    background: brand.colors.background,
    // CSS custom property references for use with className
    cssVars: {
      matrixGreen: 'var(--matrix-green)',
      bloodRed: 'var(--blood-red)',
      terminalGreen: 'var(--terminal-green)'
    }
  };
};